/* eslint-disable @typescript-eslint/naming-convention */
import { getValidChildren } from '@chakra-ui/react-utils';
import {
  chakra,
  forwardRef,
  omitThemingProps,
  SystemProps,
  SystemStyleObject,
  ThemingProps,
  useMultiStyleConfig,
  HTMLChakraProps,
} from '@chakra-ui/system';
import { cx, filterUndefined, __DEV__ } from '@chakra-ui/utils';
import { cloneElement, ReactNode } from 'react';

interface AvatarReversedGroupOptions {
  /**
   * The children of the avatar group.
   *
   * Ideally should be `Avatar` and `MoreIndicator` components
   */
  children: ReactNode;
  /**
   * The space between the avatars in the group.
   * @type SystemProps["margin"]
   */
  spacing?: SystemProps['margin'];
  /**
   * The maximum number of visible avatars
   */
  max?: number;
}

export interface AvatarReversedGroupProps
  extends AvatarReversedGroupOptions,
    Omit<HTMLChakraProps<'div'>, 'children'>,
    ThemingProps<'Avatar'> {}

/**
 * AvatarReversedGroup displays a number of avatars grouped together in a stack.
 */
export const AvatarReversedGroup = forwardRef<AvatarReversedGroupProps, 'div'>((props, ref) => {
  const styles = useMultiStyleConfig('Avatar', props);

  const { children, borderColor, max, spacing = '-0.75rem', borderRadius = 'full', ...rest } = omitThemingProps(props);

  const validChildren = getValidChildren(children);

  /**
   * get the avatars within the max
   */
  const childrenWithinMax = max ? validChildren.slice(0, max) : validChildren;

  /**
   * get the remaining avatar count
   */
  const excess = max != null && validChildren.length - max;

  const clones = childrenWithinMax.map((child, index) => {
    const isFirstAvatar = index === 0;

    const childProps = {
      marginStart: isFirstAvatar ? 0 : spacing,
      size: props.size,
      borderColor: child.props.borderColor ?? borderColor,
      showBorder: true,
    };

    return cloneElement(child, filterUndefined(childProps));
  });

  const groupStyles: SystemStyleObject = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexDirection: 'row',
  };

  const baseStyle: SystemStyleObject = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    textTransform: 'uppercase',
    fontWeight: 'medium',
    position: 'relative',
    flexShrink: 0,
  };

  const excessStyles: SystemStyleObject = {
    borderRadius,
    marginStart: spacing,
    border: '2px solid',
    boxSizing: 'content-box',
    ...baseStyle,
    ...styles.excessLabel,
    borderColor: borderColor,
  };

  return (
    <chakra.div
      ref={ref}
      role="group"
      __css={groupStyles}
      {...rest}
      className={cx('chakra-avatar__group', props.className)}
    >
      {clones}
      {excess > 0 && (
        <chakra.span className="chakra-avatar__excess" __css={excessStyles}>
          {`+${excess}`}
        </chakra.span>
      )}
    </chakra.div>
  );
});

if (__DEV__) {
  AvatarReversedGroup.displayName = 'AvatarReversedGroup';
}
